import * as React from 'react';
import { useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './input';
import { cn } from '@/lib/utils';

export interface BirthdateValue {
  month: string;
  day: string;
  year: string;
}

export type DateFormat = 'mdy' | 'dmy' | 'ymd';

export interface BirthdateInputProps {
  /** ID applied to the first input - use with label's htmlFor for accessibility */
  id?: string;
  value: BirthdateValue | null;
  onChange: (value: BirthdateValue) => void;
  disabled?: boolean;
  className?: string;
  /** Date format: 'mdy' (US), 'dmy' (Europe), 'ymd' (ISO). Defaults to 'mdy'. */
  format?: DateFormat;
}

/**
 * Expands a 2-digit year to 4-digit year
 */
function expandYear(year: string): string {
  if (year.length === 2) {
    const yearNum = parseInt(year, 10);
    return yearNum > 50 ? `19${year}` : `20${year}`;
  }
  return year;
}

/**
 * Parses a date string from common formats into BirthdateValue
 * @param text - The date string to parse
 * @param expandShortYear - If true, converts 2-digit years (19 → 2019). Use for paste operations.
 * @param preferredFormat - The format to prefer when parsing ambiguous dates (e.g., 03/04/2024)
 */
function parseDateString(
  text: string,
  expandShortYear = false,
  preferredFormat: DateFormat = 'mdy'
): BirthdateValue | null {
  // Handle ISO format YYYY-MM-DD (always unambiguous)
  const isoMatch = text.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (isoMatch) {
    return {
      year: isoMatch[1],
      month: isoMatch[2].padStart(2, '0'),
      day: isoMatch[3].padStart(2, '0'),
    };
  }

  // Handle formats with year at the end (MM/DD/YYYY or DD/MM/YYYY)
  const shortYearEndMatch = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{1,4})$/);
  if (shortYearEndMatch) {
    let year = shortYearEndMatch[3];
    if (expandShortYear && year.length === 2) {
      year = expandYear(year);
    }

    const first = shortYearEndMatch[1].padStart(2, '0');
    const second = shortYearEndMatch[2].padStart(2, '0');

    // Use preferred format to determine order
    if (preferredFormat === 'dmy') {
      return { day: first, month: second, year };
    } else {
      // Default to MDY for 'mdy' or 'ymd'
      return { month: first, day: second, year };
    }
  }

  return null;
}

type FieldType = 'month' | 'day' | 'year';

interface FieldConfig {
  type: FieldType;
  maxLength: number;
  width: string;
  placeholder: string;
  ariaLabel: string;
}

function getFieldOrder(format: DateFormat): FieldType[] {
  switch (format) {
    case 'dmy':
      return ['day', 'month', 'year'];
    case 'ymd':
      return ['year', 'month', 'day'];
    case 'mdy':
    default:
      return ['month', 'day', 'year'];
  }
}

function getSeparator(format: DateFormat): string {
  return format === 'ymd' ? '-' : '/';
}

export function BirthdateInput({
  id,
  value,
  onChange,
  disabled = false,
  className,
  format = 'mdy',
}: BirthdateInputProps) {
  const { t } = useTranslation();
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const [localMonth, setLocalMonth] = useState(value?.month ?? '');
  const [localDay, setLocalDay] = useState(value?.day ?? '');
  const [localYear, setLocalYear] = useState(value?.year ?? '');

  const fieldOrder = useMemo(() => getFieldOrder(format), [format]);
  const separator = useMemo(() => getSeparator(format), [format]);

  const refs = useMemo(
    () => ({
      month: monthRef,
      day: dayRef,
      year: yearRef,
    }),
    []
  );

  const fieldConfigs = useMemo((): Record<FieldType, FieldConfig> => {
    return {
      month: {
        type: 'month',
        maxLength: 2,
        width: 'w-14',
        placeholder: t('birthdateInput.month', 'MM'),
        ariaLabel: t('birthdateInput.monthAriaLabel', 'Month (2 digits)'),
      },
      day: {
        type: 'day',
        maxLength: 2,
        width: 'w-14',
        placeholder: t('birthdateInput.day', 'DD'),
        ariaLabel: t('birthdateInput.dayAriaLabel', 'Day (2 digits)'),
      },
      year: {
        type: 'year',
        maxLength: 4,
        width: 'w-20',
        placeholder: t('birthdateInput.year', 'YYYY'),
        ariaLabel: t('birthdateInput.yearAriaLabel', 'Year (4 digits)'),
      },
    };
  }, [t]);

  // Sync local state with external value when it changes
  React.useEffect(() => {
    setLocalMonth(value?.month ?? '');
    setLocalDay(value?.day ?? '');
    setLocalYear(value?.year ?? '');
  }, [value?.month, value?.day, value?.year]);

  const emitChange = useCallback(
    (month: string, day: string, year: string) => {
      onChange({ month, day, year });
    },
    [onChange]
  );

  const getNextField = useCallback(
    (currentField: FieldType): FieldType | null => {
      const currentIndex = fieldOrder.indexOf(currentField);
      if (currentIndex < fieldOrder.length - 1) {
        return fieldOrder[currentIndex + 1];
      }
      return null;
    },
    [fieldOrder]
  );

  const getPrevField = useCallback(
    (currentField: FieldType): FieldType | null => {
      const currentIndex = fieldOrder.indexOf(currentField);
      if (currentIndex > 0) {
        return fieldOrder[currentIndex - 1];
      }
      return null;
    },
    [fieldOrder]
  );

  const handleMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
      setLocalMonth(digits);
      emitChange(digits, localDay, localYear);

      // Auto-advance: 2 digits OR single digit >= 2 (months 1 must wait for second digit)
      if (
        digits.length === 2 ||
        (digits.length === 1 && parseInt(digits, 10) >= 2)
      ) {
        const nextField = getNextField('month');
        if (nextField) refs[nextField].current?.focus();
      }
    },
    [emitChange, localDay, localYear, getNextField, refs]
  );

  const handleDayChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
      setLocalDay(digits);
      emitChange(localMonth, digits, localYear);

      // Auto-advance: 2 digits OR single digit >= 4 (days 1-3 must wait for second digit)
      if (
        digits.length === 2 ||
        (digits.length === 1 && parseInt(digits, 10) >= 4)
      ) {
        const nextField = getNextField('day');
        if (nextField) refs[nextField].current?.focus();
      }
    },
    [emitChange, localMonth, localYear, getNextField, refs]
  );

  const handleYearChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
      setLocalYear(digits);
      emitChange(localMonth, localDay, digits);

      // Auto-advance when 4 digits entered
      if (digits.length === 4) {
        const nextField = getNextField('year');
        if (nextField) refs[nextField].current?.focus();
      }
    },
    [emitChange, localMonth, localDay, getNextField, refs]
  );

  const handleChange = useMemo(
    () => ({
      month: handleMonthChange,
      day: handleDayChange,
      year: handleYearChange,
    }),
    [handleMonthChange, handleDayChange, handleYearChange]
  );

  const localValues = useMemo(
    () => ({
      month: localMonth,
      day: localDay,
      year: localYear,
    }),
    [localMonth, localDay, localYear]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, field: FieldType) => {
      if (
        e.key === 'Backspace' &&
        (e.target as HTMLInputElement).value === ''
      ) {
        e.preventDefault();
        const prevField = getPrevField(field);
        if (prevField) refs[prevField].current?.focus();
      }
    },
    [getPrevField, refs]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const text = e.clipboardData.getData('text').trim();
      // Use expandShortYear=true for paste to handle 2-digit years like "03/15/90"
      const parsed = parseDateString(text, true, format);

      if (parsed) {
        e.preventDefault();
        setLocalMonth(parsed.month);
        setLocalDay(parsed.day);
        setLocalYear(parsed.year);
        emitChange(parsed.month, parsed.day, parsed.year);
        // Focus the last field in the order
        const lastField = fieldOrder[fieldOrder.length - 1];
        refs[lastField].current?.focus();
      }
    },
    [emitChange, format, fieldOrder, refs]
  );

  return (
    <fieldset
      className={cn('flex items-center justify-center gap-1', className)}
      onPaste={handlePaste}
    >
      <legend className="sr-only">
        {t('birthdateInput.legend', 'Date of Birth')}
      </legend>
      {fieldOrder.map((fieldType, index) => {
        const config = fieldConfigs[fieldType];
        const isFirst = index === 0;
        return (
          <React.Fragment key={fieldType}>
            {index > 0 && (
              <span aria-hidden="true" className="text-muted-foreground">
                {separator}
              </span>
            )}
            <Input
              ref={refs[fieldType]}
              id={isFirst ? id : `dob-${fieldType}`}
              aria-label={config.ariaLabel}
              placeholder={config.placeholder}
              value={localValues[fieldType]}
              onChange={handleChange[fieldType]}
              onKeyDown={(e) => handleKeyDown(e, fieldType)}
              disabled={disabled}
              autoComplete="off"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={config.maxLength}
              className={cn(config.width, 'text-center')}
            />
          </React.Fragment>
        );
      })}
    </fieldset>
  );
}

/**
 * Validates a BirthdateValue and returns an error message or null if valid
 */
export function validateBirthdate(value: BirthdateValue | null): string | null {
  if (!value) return 'Please enter a date';

  const { month, day, year } = value;

  if (!month || !day || !year) {
    return 'Please enter a complete date';
  }

  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  const y = parseInt(year, 10);

  if (isNaN(m) || m < 1 || m > 12) {
    return 'Month must be 01-12';
  }

  if (isNaN(d) || d < 1 || d > 31) {
    return 'Day must be 01-31';
  }

  if (isNaN(y) || y < 1900 || y > new Date().getFullYear()) {
    return 'Invalid year';
  }

  // Validate the actual date
  const date = new Date(y, m - 1, d);
  if (date.getMonth() !== m - 1) {
    return 'Invalid date';
  }

  if (date > new Date()) {
    return 'Date cannot be in the future';
  }

  return null;
}

/**
 * Converts BirthdateValue to string format based on the specified format
 * Returns empty string only if all fields are empty
 * @param value - The birthdate value to convert
 * @param format - The date format to use (default: 'mdy')
 */
export function birthdateToString(
  value: BirthdateValue | null,
  format: DateFormat = 'mdy'
): string {
  if (!value) return '';
  // Only return empty if ALL fields are empty
  if (!value.month && !value.day && !value.year) {
    return '';
  }
  // Preserve partial data by always serializing
  const month = value.month || '';
  const day = value.day || '';
  const year = value.year || '';
  const sep = format === 'ymd' ? '-' : '/';

  switch (format) {
    case 'dmy':
      return `${day}${sep}${month}${sep}${year}`;
    case 'ymd':
      return `${year}${sep}${month}${sep}${day}`;
    case 'mdy':
    default:
      return `${month}${sep}${day}${sep}${year}`;
  }
}

/**
 * Parses a date string to BirthdateValue based on the specified format
 * Handles partial data (e.g., "03/15/" or "//1990")
 * @param dateStr - The date string to parse
 * @param format - The date format to use for parsing (default: 'mdy')
 */
export function stringToBirthdate(
  dateStr: string,
  format: DateFormat = 'mdy'
): BirthdateValue | null {
  if (!dateStr) return null;

  // First try the standard parseDateString for complete dates
  const parsed = parseDateString(dateStr, false, format);
  if (parsed) return parsed;

  // Handle partial data based on format
  const sep = format === 'ymd' ? '-' : '/';
  const parts = dateStr.split(sep);

  // Fallback: try splitting by both separators for partial data
  const altParts =
    parts.length !== 3 ? dateStr.split(format === 'ymd' ? '/' : '-') : parts;

  if (altParts.length === 3) {
    switch (format) {
      case 'dmy':
        return {
          day: altParts[0] || '',
          month: altParts[1] || '',
          year: altParts[2] || '',
        };
      case 'ymd':
        return {
          year: altParts[0] || '',
          month: altParts[1] || '',
          day: altParts[2] || '',
        };
      case 'mdy':
      default:
        return {
          month: altParts[0] || '',
          day: altParts[1] || '',
          year: altParts[2] || '',
        };
    }
  }

  return null;
}
